'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import Link from 'next/link';
import './supplychain.css';

/* Saari styling supplychain.css mein hai.
  Hero + Final CTA background image ka path CSS mein hai:
  supplychain.css -> ".supply-hero-bg-image" aur ".final-cta-bg"

  Image (public/assets/images/ mein daalo):
  - supply-chain-hero.jpg -> Hero background + Final CTA background

  Links: "Intelligence Areas" cards ke `href` field me badlo (intelligenceAreas array).
  Jin cards ka page nahi hai unme href nahi hai, wo plain card rahenge. */

type IconName =
  | 'question'
  | 'inventory'
  | 'location'
  | 'search'
  | 'trend'
  | 'options'
  | 'action'
  | 'value'
  | 'demand'
  | 'procurement'
  | 'production'
  | 'logistics'
  | 'asset'
  | 'finance'
  | 'clock'
  | 'data'
  | 'check';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    question: <><circle cx="12" cy="12" r="9" /><path d="M9.6 9a2.5 2.5 0 1 1 4.2 1.8c-1 .8-1.8 1.2-1.8 2.7" /><path d="M12 16.8h.01" /></>,
    inventory: <><path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5z" /><path d="M4 7.5 12 12l8-4.5M12 12v9" /></>,
    location: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>,
    trend: <><path d="M4 17 10 11l4 4 6-8" /><path d="M15 7h5v5" /></>,
    options: <><circle cx="12" cy="12" r="9" /><path d="M8 12h8M12 8v8" /></>,
    action: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.7 2.7L16 9.5" /></>,
    value: <><path d="M12 3v18M16 7.5c-.8-1-2.1-1.5-4-1.5-2.3 0-4 1.1-4 2.8 0 4.2 8 1.7 8 5.4 0 1.7-1.6 2.8-4 2.8-1.9 0-3.3-.6-4-1.7" /></>,
    demand: <><path d="M4 18V9M10 18V5M16 18v-7M22 18V3" /></>,
    procurement: <><path d="M4 5h16v14H4z" /><path d="M8 9h8M8 13h5M8 17h8" /></>,
    production: <><path d="M4 19V9l5-4 3 3 4-3 4 4v10z" /><path d="M8 19v-5h8v5" /></>,
    logistics: <><path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></>,
    asset: <><path d="M4 18V8l8-4 8 4v10l-8 3z" /><path d="M8 10h8M8 14h8" /></>,
    finance: <><circle cx="12" cy="12" r="9" /><path d="M12 6v12M15 8.5c-.6-.8-1.5-1.2-2.8-1.2-1.7 0-2.8.8-2.8 2 0 3.1 5.8 1.3 5.8 4.3 0 1.3-1.3 2.1-3 2.1-1.3 0-2.4-.4-3-1.2" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    data: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 15v-3M12 15V8M16 15v-5" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" /></>,
  };

  return (
    <svg
      className="supply-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const questions = [
  'Why is inventory increasing when sales aren’t?',
  'Which inventory is excess, slow-moving or obsolete?',
  'What should we replenish — and stop buying?',
  'Do we really need to buy this spare?',
  'Why is forecast accuracy falling?',
  'What will stock out next?',
  'Why are purchase prices increasing?',
  'Which suppliers are affecting service or cost?',
  'Which POs are likely to be delayed?',
  'Why does our production plan keep changing?',
  'Which assets are likely to fail?',
  'Why is maintenance cost rising without better uptime?',
  'Where is supply-chain working capital trapped?',
];

const flow: { label: string; icon: IconName; tone: 'navy' | 'blue' | 'green' | 'amber' }[] = [
  { label: 'Customer / Demand', icon: 'demand', tone: 'navy' },
  { label: 'Plan', icon: 'data', tone: 'blue' },
  { label: 'Source', icon: 'procurement', tone: 'blue' },
  { label: 'Make', icon: 'production', tone: 'green' },
  { label: 'Move', icon: 'logistics', tone: 'green' },
  { label: 'Inventory', icon: 'inventory', tone: 'amber' },
  { label: 'Customer / Sale', icon: 'action', tone: 'blue' },
  { label: 'Cash', icon: 'finance', tone: 'green' },
  { label: 'Assets', icon: 'asset', tone: 'blue' },
];
const flowCaption = ['Plant', 'Equipment', 'Spares', 'Maintenance'];

const monitoring: { q: string; a: string; icon: IconName; tone: string }[] = [
  { q: 'What happened?', a: 'See the current situation', icon: 'trend', tone: 'rose' },
  { q: 'Where?', a: 'Find what and where', icon: 'location', tone: 'amber' },
  { q: 'Why?', a: 'Understand the root cause', icon: 'search', tone: 'blue' },
  { q: 'What will happen next?', a: 'Predict and simulate', icon: 'trend', tone: 'indigo' },
  { q: 'What are our options?', a: 'Evaluate alternatives', icon: 'options', tone: 'purple' },
  { q: 'What action should we take?', a: 'Recommend and execute', icon: 'action', tone: 'cyan' },
  { q: 'Did it create value?', a: 'Measure the outcome', icon: 'value', tone: 'green' },
];

const insightSteps: { title: string; text: string; icon: IconName; tone: string }[] = [
  { title: 'Where is it?', text: 'Product × Plant × Warehouse × Region', icon: 'location', tone: 'blue' },
  { title: 'Why is it there?', text: 'Analyse drivers across demand, supply, production and policy', icon: 'clock', tone: 'amber' },
  { title: 'What can we change?', text: 'Buy / Make / Transfer / Hold / Stop', icon: 'options', tone: 'indigo' },
];
const insightValue = { title: 'Value', text: 'Lower inventory, better service, improved cash flow', icon: 'value' as IconName };

/* href = card ka link. Jin areas ka page nahi hai (Production, Logistics) unme href nahi hai. */
const intelligenceAreas: { icon: IconName; title: string; tags: string[]; href?: string }[] = [
  { icon: 'demand', title: 'Demand & Forecast Intelligence', tags: ['Demand', 'Forecast Bias', 'Variability', 'What-if'], href: '/solutions/inventory-intelligence' },
  { icon: 'inventory', title: 'Inventory Intelligence', tags: ['Stock', 'Ageing', 'Excess', 'Obsolescence', 'Stock-out'], href: '/solutions/inventory-intelligence' },
  { icon: 'procurement', title: 'Procurement Intelligence', tags: ['PO', 'Price', 'Supplier', 'Lead Time', 'Exceptions'], href: '/solutions/spend-ventor' },
  { icon: 'production', title: 'Production Intelligence', tags: ['Plan', 'Capacity', 'Material', 'Schedule Variance'] },
  { icon: 'logistics', title: 'Logistics Intelligence', tags: ['Warehouse', 'Shipment', 'Route', 'Freight', 'Service'] },
  { icon: 'asset', title: 'Asset & Maintenance Intelligence', tags: ['Equipment', 'Failure', 'Spares', 'Work Orders', 'Downtime'], href: '/solutions/asset-intelligence' },
];

const bars = [
  { label: '6 months ago', display: '₹ 100 Cr', value: 100, tone: 'old' },
  { label: 'Current', display: '₹ 128 Cr', value: 128, tone: 'new' },
];
const barMax = 128;

const drivers: { label: string; value: string; tone: 'slate' | 'amber' | 'rose' }[] = [
  { label: 'Demand (flat)', value: '0%', tone: 'slate' },
  { label: 'Forecast bias', value: '+6%', tone: 'rose' },
  { label: 'Purchase orders', value: '+8%', tone: 'rose' },
  { label: 'Production', value: '+7%', tone: 'rose' },
  { label: 'Slow-moving SKUs', value: '+4%', tone: 'amber' },
  { label: 'Safety stock', value: '+2%', tone: 'amber' },
  { label: 'Customer mix change', value: '+1%', tone: 'amber' },
];

const financeChains = [
  {
    title: 'Inventory & Working Capital',
    badge: 'Cash',
    steps: ['More Inventory', 'More Working Capital', 'More Storage Cost', 'More Ageing', 'Higher Obsolescence Risk'],
    result: 'Less Cash',
  },
  {
    title: 'Asset Reliability',
    badge: 'Profit',
    steps: ['Asset Failure', 'Downtime', 'Lost Capacity', 'Service / Production Impact', 'Revenue / Cost Impact'],
    result: 'Lower Profitability',
  },
];

const problems = [
  'Why is inventory increasing when sales aren’t?',
  'Which inventory is excess, slow-moving or obsolete?',
  'What should we replenish — and stop buying?',
  'Do we really need to buy this spare?',
  'Why is forecast accuracy falling?',
  'What will stock out next?',
  'Why are purchase prices increasing?',
  'Where is supplier performance costing us money?',
  'Which POs are likely to be delayed?',
  'Why does our production plan keep changing?',
  'Which assets are likely to fail?',
  'Why is maintenance cost rising without better uptime?',
  'Where is supply-chain working capital trapped?',
];

const systems = ['SAP S/4HANA', 'Planning', 'Procurement', 'WMS', 'Production', 'EAM / PM', 'Logistics', 'Sales', 'External Data'];

/* ===================== SCROLL ANIMATION ===================== */
/* Sirf fade-up (neeche se upar), zoom aur bar-grow. Left/right slide nahi. */
const animGroups: [string, 'fade-up' | 'zoom' | 'grow'][] = [
  ['.sec-head, .opview-head', 'fade-up'],
  ['.q-card', 'fade-up'],
  ['.flow-step', 'zoom'],
  ['.flow-caption', 'fade-up'],
  ['.pipeline', 'fade-up'],
  ['.pipeline-row', 'fade-up'],
  ['.coverage-card', 'zoom'],
  ['.cockpit', 'fade-up'],
  ['.wq-chart', 'zoom'],
  ['.stat-card', 'zoom'],
  ['.bar', 'grow'],
  ['.chain-card', 'fade-up'],
  ['.chain-step', 'fade-up'],
  ['.ps-card', 'fade-up'],
  ['.problem-row', 'fade-up'],
  ['.sys-chip', 'zoom'],
  ['.stack-step', 'fade-up'],
  ['.final-cta-box', 'zoom'],
];

function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    animGroups.forEach(([selector, type]) => {
      const perParent = new Map<Element, number>();
      el.querySelectorAll<HTMLElement>(selector).forEach((node) => {
        const parent = node.parentElement as Element;
        const i = perParent.get(parent) ?? 0;
        perParent.set(parent, i + 1);

        node.classList.add('rv', `rv--${type}`);
        node.style.setProperty('--rv-delay', `${(i % 6) * 80}ms`); // stagger
        io.observe(node);
      });
    });

    return () => io.disconnect();
  }, [root]);
}

/* ============================== PAGE ============================== */

export default function Supply() {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="supply-page" ref={pageRef}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="supply-hero" data-purpose="hero-section">
          <div className="supply-hero-bg-image"></div>
          <div className="supply-hero-bg-overlay"></div>
          <div className="supply-hero-grid"></div>
          <div className="supply-hero-glow supply-hero-glow--left"></div>
          <div className="supply-hero-glow supply-hero-glow--right"></div>

          <div className="op-container op-container--left supply-hero-inner">
            <div className="supply-hero-layout">
              <div className="supply-hero-content">

                <h1 className="supply-hero-title">
                  See What Is Moving.
                  <br />
                  Understand What Is Stuck.
                  <br />
                  Know What To Do Next.
                </h1>

                <p className="supply-hero-text">
                  From demand and inventory to procurement, production, logistics and assets, Orane
                  connects the flow of the enterprise to help teams identify exceptions, understand
                  causes and act earlier.
                </p>

                <div className="supply-hero-actions">
                  <Link className="btn-primary" href="/tellusyourproblem#tell-details">
                    Tell Us Your Supply Chain Problem
                    <Arrow />
                  </Link>
                  <button className="btn-glass" type="button">
                    See How It Works
                    <span className="play-icon">▶</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================== THE QUESTIONS LEADERS ASK ================== */}
        <section className="op-section op-section1 op-section--white" data-purpose="leader-questions">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Real Decisions</div>
              <h2 className="sec-title">The Questions Leaders Actually Ask</h2>
              <p className="sec-sub">Real questions. Real decisions. Real impact.</p>
            </div>

            <div className="q-grid">
              {questions.map((q) => (
                <div className="q-card" key={q}>
                  <span className="q-mark">?</span>
                  <p className="q-text">{q}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =================== END-TO-END SUPPLY CHAIN FLOW =================== */}
        <section className="op-section op-section--slate" data-purpose="end-to-end-flow">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Connected Flow</div>
              <h2 className="sec-title">The End-to-End Supply Chain Flow</h2>
              <p className="sec-sub sec-sub--base">
                A connected flow of materials, capacity, assets and cash.
              </p>
            </div>

            <div className="flow-scroll">
              <div className="flow-track">
                {flow.map((node) => (
                  <div className={`flow-step flow-step--${node.tone}`} key={node.label}>
                    <Icon name={node.icon} />
                    <span className="flow-label">{node.label}</span>
                  </div>
                ))}
              </div>
              <div className="flow-caption">
                {flowCaption.map((c) => (
                  <span className="flow-chip" key={c}>{c}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============== MONITORING TO INTELLIGENCE / INSIGHT TO ACTION ============== */}
        <section className="op-section op-section--white" data-purpose="monitoring-to-intelligence">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Decision Ladder</div>
              <h2 className="sec-title">From Monitoring to Intelligence</h2>
              <p className="sec-sub">Move from hindsight to proactive action.</p>
            </div>

            <div className="ladder-grid">
              <div className="pipeline">
                <h3 className="pipeline-head">
                  <span>Seven Questions, One Flow</span>
                  <span className="pipeline-count">{monitoring.length} Stages</span>
                </h3>
                {monitoring.map((row) => (
                  <div className="pipeline-row" key={row.q}>
                    <div className={`pipeline-num tone-${row.tone}`}>
                      <Icon name={row.icon} />
                    </div>
                    <div className="pipeline-label">{row.q}</div>
                    <span className="pipeline-stage">{row.a}</span>
                  </div>
                ))}
              </div>

              <div className="pipeline">
                <h3 className="pipeline-head">
                  <span>From Insight to Action</span>
                  <span className="pipeline-count">4 Steps</span>
                </h3>
                {insightSteps.map((row) => (
                  <div className="pipeline-row pipeline-row--top" key={row.title}>
                    <div className={`pipeline-num tone-${row.tone}`}>
                      <Icon name={row.icon} />
                    </div>
                    <div className="pipeline-body">
                      <div className="pipeline-label">{row.title}</div>
                      <p className="pipeline-desc">{row.text}</p>
                    </div>
                  </div>
                ))}
                <div className="pipeline-row pipeline-row--top pipeline-row--final">
                  <div className="pipeline-num pipeline-num--final">
                    <Icon name={insightValue.icon} />
                  </div>
                  <div className="pipeline-body">
                    <div className="pipeline-label">{insightValue.title}</div>
                    <p className="pipeline-desc">{insightValue.text}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =================== INTELLIGENCE AREAS =================== */}
        <section className="op-section op-section--slate" data-purpose="intelligence-areas">
          <div className="op-container op-container--left">
            <div className="sec-head">
              <div className="sec-badge">Coverage Matrix</div>
              <h2 className="sec-title">Supply Chain &amp; Assets Intelligence Areas</h2>
              <p className="sec-sub">Focus on what matters. Solve real business problems.</p>
            </div>

            <div className="coverage-grid">
              {intelligenceAreas.map((item) => {
                const body = (
                  <>
                    <div>
                      <div className="coverage-icon">
                        <Icon name={item.icon} />
                      </div>
                      <h3 className="coverage-title">{item.title}</h3>
                      <div className="tag-list">
                        {item.tags.map((t) => (
                          <span className="tag" key={t}>{t}</span>
                        ))}
                      </div>
                    </div>
                    <div className="coverage-foot">
                      <span>Intelligence Area</span>
                      <span className="coverage-dot"></span>
                    </div>
                  </>
                );
                /* href na ho to card plain div rahega (click nahi hoga) */
                return item.href ? (
                  <Link className="coverage-card" key={item.title} href={item.href}>
                    {body}
                  </Link>
                ) : (
                  <div className="coverage-card" key={item.title}>
                    {body}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ======================== WORKED QUESTION ======================== */}
        <section className="op-section op-section--white" data-purpose="worked-question">
          <div className="op-container">
            <div className="opview-head">
              <div>
                <div className="sec-badge">Worked Example</div>
                <h2 className="sec-title">A Worked Question</h2>
                <p className="sec-sub sec-sub--base">
                  One leadership question, broken down into the drivers behind it.
                </p>
              </div>
              <div className="opview-live">
                <span className="opview-live-dot"></span>
                Illustrative Data
              </div>
            </div>

            <div className="cockpit">
              <div className="cockpit-bar">
                <div className="cockpit-bar-left">
                  <div className="cockpit-light cockpit-light--red"></div>
                  <div className="cockpit-light cockpit-light--amber"></div>
                  <div className="cockpit-light cockpit-light--green"></div>
                  <span className="cockpit-name">ORANE-SC-VIEW // INVENTORY DRIVER ANALYSIS</span>
                </div>
                <div className="cockpit-bar-right">
                  <span className="cockpit-chip">MODE: ILLUSTRATIVE</span>
                </div>
              </div>

              <h3 className="cockpit-question">“Why is inventory increasing when sales aren’t?”</h3>

              <div className="wq-grid">
                <div className="wq-chart">
                  <div className="stat-card-head">
                    <span>Inventory</span>
                    <span className="stat-dot stat-dot--amber"></span>
                  </div>
                  <div className="stat-value stat-value--rose">↑ 28%</div>

                  <div className="bars">
                    {bars.map((b) => (
                      <div className="bar-col" key={b.label}>
                        <div className="bar-area">
                          <div
                            className={`bar bar--${b.tone}`}
                            style={{ height: `${(b.value / barMax) * 100}%` }}
                          >
                            {b.display}
                          </div>
                        </div>
                        <span className="bar-cap">{b.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="driver-grid">
                  {drivers.map((d) => (
                    <div className="stat-card" key={d.label}>
                      <div className="stat-card-head">
                        <span>{d.label}</span>
                        <span className={`stat-dot stat-dot--${d.tone}`}></span>
                      </div>
                      <div className={`stat-value stat-value--${d.tone}`}>{d.value}</div>
                      <div className="stat-note stat-note--slate">
                        <span>Key driver</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== CONNECT OPERATIONS TO FINANCE ===================== */}
        <section className="op-section op-section--slate" data-purpose="operations-to-finance">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Financial Impact</div>
              <h2 className="sec-title">Connect Operations to Finance</h2>
              <p className="sec-sub sec-sub--base">
                Operational decisions directly impact financial outcomes.
              </p>
            </div>

            <div className="chain-grid">
              {financeChains.map((chain) => (
                <div className="chain-card" key={chain.title}>
                  <div>
                    <div className="card-head">
                      <h3 className="chain-title">{chain.title}</h3>
                      <span className="chain-badge">{chain.badge}</span>
                    </div>
                    <div className="chain-steps">
                      {chain.steps.map((step, i) => (
                        <div key={step}>
                          <div className="chain-step">
                            <span className="chain-step-num">{i + 1}</span>
                            {step}
                          </div>
                          <div className="chain-connector"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="chain-result">{chain.result}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================== 52 PROBLEMS + ENTERPRISE SYSTEMS ================== */}
        <section className="op-section op-section--white" data-purpose="problems-and-systems">
          <div className="op-container">
            <div className="ps-grid">
              <div className="ps-card">
                <div className="sec-badge">Relevant 52 Business Problems (01–13)</div>
                <h2 className="sec-title sec-title--sm">Key Supply Chain &amp; Asset Questions</h2>
                <p className="sec-sub sec-sub--base">Explore the full library.</p>

                <div className="problem-list">
                  {problems.map((p, i) => (
                    <div className="problem-row" key={p}>
                      <span className="problem-num">{String(i + 1).padStart(2, '0')}</span>
                      <span className="problem-text">{p}</span>
                    </div>
                  ))}
                </div>

                <div className="ps-foot">
                  <Link className="btn-small" href="/52problem">
                    Explore All 52 Business Problems
                    <Arrow />
                  </Link>
                </div>
              </div>

              <div className="ps-card">
                <div className="sec-badge">Enterprise Systems &amp; Data</div>
                <h2 className="sec-title sec-title--sm">Bring Data Together</h2>
                <p className="sec-sub sec-sub--base">
                  Bring data together. Add business context. Deliver intelligence.
                </p>

                <div className="sys-grid">
                  {systems.map((s) => (
                    <span className="sys-chip" key={s}>{s}</span>
                  ))}
                </div>

                <div className="stack">
                  <div className="stack-step">Trusted Data</div>
                  <span className="stack-arrow">↓</span>
                  <div className="stack-step">
                    Business Context
                    <small>Master Data • Process • Rules • Industry Knowledge</small>
                  </div>
                  <span className="stack-arrow">↓</span>
                  <div className="stack-step stack-step--final">Supply Chain &amp; Assets Intelligence</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="final-cta" data-purpose="final-call-to-action">
          <div className="final-cta-bg"></div>
          <div className="final-cta-overlay"></div>
          <div className="op-container final-cta-inner">
            <div className="final-cta-box">
              <div className="final-cta-left">
                <span className="final-cta-eyebrow">SEE THE FLOW. FIND THE EXCEPTION.</span>
                <h2 className="final-cta-title">Don’t Manage the Supply Chain Through More Reports.</h2>
                <p className="final-cta-text">See the flow. Find the exception. Understand the cause. Act.</p>
                <p className="final-cta-copy">
                  Bring us a supply chain, procurement, inventory, production, logistics or asset
                  question. We’ll work backwards from the business outcome to the data, intelligence and
                  actions required.
                </p>
              </div>
              <div className="final-cta-right">
                <Link className="btn-primary btn-primary--cta" href="/tellusyourproblem#tell-details">
                  Tell Us Your Supply Chain Problem
                  <Arrow />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}